import '../styles/OperationsAccountServicingBlock.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

function symbolFor(currency) {
  if (!currency) return '';
  const key = String(currency).toUpperCase();
  return CURRENCY_SYMBOLS[key] || key + ' ';
}

function formatBalance(value, currency) {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '—';
  return symbolFor(currency) + amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatAccountBalance(value, currency) {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'string' && value.trim() === '') return 'Not provided';
  return formatBalance(value, currency);
}

function statusClass(status) {
  const key = typeof status === 'string' ? status.toLowerCase() : '';
  if (key === 'active') return 'oasb-status is-active';
  if (key === 'pending') return 'oasb-status is-pending';
  return 'oasb-status is-neutral';
}

function ClockIcon({ spinning }) {
  return (
    <svg
      className={spinning ? 'oasb-spin' : undefined}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 11a8 8 0 1 0-2.3 6.4" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

function AccountsMicroLabelHeader({ count, busy, onRefresh }) {
  return (
    <div className="oasb-head">
      <div className="oasb-head-lockup">
        <span className="oasb-dial" aria-hidden="true" />
        <p className="oasb-micro">
          <strong>Client accounts</strong>
          {typeof count === 'number' ? ` · ${count} on file` : ''}
        </p>
      </div>
      <button
        type="button"
        className="oasb-btn"
        onClick={onRefresh}
        disabled={busy}
        aria-label="Refresh client account records"
      >
        <ClockIcon spinning={busy} />
        <span>{busy ? 'Refreshing' : 'Refresh'}</span>
      </button>
    </div>
  );
}

function AccountsRuledRows({ records }) {
  return (
    <ul className="oasb-ledger">
      {records.map((record) => (
        <li className="oasb-row" key={record.id} tabIndex={0}>
          <span className="oasb-id-block">
            <span className="oasb-holder">
              {record.holderName || 'Unnamed holder'}
            </span>
            <span className="oasb-acct">{record.id}</span>
            <span className="oasb-note">
              {record.email || 'No contact email on record'}
            </span>
          </span>
          <span className="oasb-value-block">
            <span className={statusClass(record.status)}>
              {record.status || 'unknown'}
            </span>
            <span className="oasb-balance">
              {formatAccountBalance(record.balance, record.currency)}
              <span className="oasb-caption">
                Available {formatAccountBalance(record.availableBalance, record.currency)}
              </span>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function AccountsConditionalStates({ onRetry }) {
  return (
    <div className="oasb-state is-error" role="alert">
      <span className="oasb-state-rule" aria-hidden="true" />
      <h3 className="oasb-state-title">Accounts unavailable</h3>
      <p className="oasb-state-body">
        The client account ledger could not be read. Check the connection and
        try the load again.
      </p>
      <button type="button" className="oasb-btn" onClick={onRetry}>
        <ClockIcon spinning={false} />
        <span>Retry</span>
      </button>
    </div>
  );
}

function LoadingRows() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div className="oasb-skel-row" key={i}>
          <span className="oasb-skel-line is-mid" />
          <span className="oasb-skel-value">
            <span className="oasb-skel-line is-value" />
          </span>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="oasb-state">
      <span className="oasb-state-rule" aria-hidden="true" />
      <h3 className="oasb-state-title">No client accounts to service</h3>
      <p className="oasb-state-body">
        No client account records are on file yet. Once a client completes
        enrollment, their account appears here for servicing.
      </p>
    </div>
  );
}

function OperationsAccountServicingBlock() {
  const { data, loading, error, refresh } = useProjectCollection('client_accounts');
  const [refreshing, setRefreshing] = useState(false);

  const records = Array.isArray(data) ? data : [];
  const busy = loading || refreshing;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  let body;
  if (loading) {
    body = <LoadingRows />;
  } else if (error) {
    body = <AccountsConditionalStates onRetry={handleRefresh} />;
  } else if (records.length === 0) {
    body = <EmptyState />;
  } else {
    body = <AccountsRuledRows records={records} />;
  }

  return (
    <section className="oasb-root" aria-label="Client accounts servicing">
      <div className="oasb-inner">
        <div className="oasb-panel">
          <AccountsMicroLabelHeader
            count={loading ? undefined : records.length}
            busy={busy}
            onRefresh={handleRefresh}
          />
          <p className="oasb-sr" role="status" aria-live="polite">
            {loading
              ? 'Loading client account records'
              : error
              ? 'Client account records failed to load'
              : `Client account records loaded: ${records.length}`}
          </p>
          {body}
        </div>
      </div>
    </section>
  );
}

export default OperationsAccountServicingBlock;